import { Box, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useEffect } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
} from "react-router-dom";
import DriverDetailsGuard from "./components/DriverDetailsGuard";
import Footer from "./components/Footer";
import Header from "./components/Header";
import SkeletonLoader from "./components/SkeletonLoader";
import { UserProvider, useUserContext } from "./contexts/UserContext";
import AddReportPage from "./pages/AddReportPage";
import AdminDashboard from "./pages/AdminDashboard";
import Analytics from "./pages/Analytics";
import AssignedCars from "./pages/AssignedCars";
import CarDetailManagement from "./pages/CarDetailManagement";
import CarForm from "./pages/CarForm";
import CarSearch from "./pages/CarSearch";
import Dashboard from "./pages/Dashboard";
import DataDeletion from "./pages/DataDeletion";
import DriverDetails from "./pages/DriverDetails";
import DriverDetailsCompletion from "./pages/DriverDetailsCompletion";
import DriverDriveRequests from "./pages/DriverDriveRequests";
import DriverSearch from "./pages/DriverSearch";
import EnhancedLoginForm from "./pages/EnhancedLoginForm";
import EnhancedSignUpForm from "./pages/EnhancedSignUpForm";
import Home from "./pages/Home";
import HowItWorks from "./pages/HowItWorks";
import OwnerDriveRequests from "./pages/OwnerDriveRequests";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ProfilePage from "./pages/Profile";
import ProfileCompletion from "./pages/ProfileCompletion";
import Rewards from "./pages/Rewards";
import SignupComplete from "./pages/SignupComplete";
import TermsOfUse from "./pages/TermsOfUse";
import theme from "./theme";

// Component to handle dynamic title updates
function DocumentTitle() {
  const location = useLocation();
  const { profile } = useUserContext();

  useEffect(() => {
    const baseTitle = "mo kumbi";
    let pageTitle = baseTitle;

    // Update title based on current route
    switch (location.pathname) {
      case "/":
        pageTitle = baseTitle;
        break;
      case "/login":
        pageTitle = `${baseTitle} - Login`;
        break;
      case "/signup":
        pageTitle = `${baseTitle} - Sign Up`;
        break;
      case "/signup-complete":
        pageTitle = `${baseTitle} - Sign Up Complete`;
        break;
      case "/complete-profile":
        pageTitle = `${baseTitle} - Complete Profile`;
        break;
      case "/how-it-works":
        pageTitle = `${baseTitle} - How It Works`;
        break;
      case "/privacy-policy":
        pageTitle = `${baseTitle} - Privacy Policy`;
        break;
      case "/terms-of-use":
        pageTitle = `${baseTitle} - Terms of Use`;
        break;
      case "/data-deletion":
        pageTitle = `${baseTitle} - Data Deletion`;
        break;
      case "/rewards":
        pageTitle = `${baseTitle} - Rewards`;
        break;
      case "/admin":
        pageTitle = `${baseTitle} - Admin Dashboard`;
        break;
      case "/analytics":
        pageTitle = `${baseTitle} - Analytics`;
        break;
      case "/assigned-cars":
        pageTitle = `${baseTitle} - Assigned Cars`;
        break;
      case "/search-cars":
        pageTitle = `${baseTitle} - Search Cars`;
        break;
      case "/drive-requests":
        pageTitle = `${baseTitle} - Drive Requests`;
        break;
      case "/cars/new":
        pageTitle = `${baseTitle} - Add New Car`;
        break;
      default:
        if (location.pathname.startsWith("/cars/")) {
          if (location.pathname.includes("/edit")) {
            pageTitle = `${baseTitle} - Edit Car`;
          } else {
            pageTitle = `${baseTitle} - Car Details`;
          }
        }
        break;
    }

    // Add user type context if available
    if (profile?.user_type) {
      pageTitle += ` (${
        profile.user_type.charAt(0).toUpperCase() + profile.user_type.slice(1)
      })`;
    }

    document.title = pageTitle;
  }, [location.pathname, profile?.user_type]);

  return null;
}

function AppContent() {
  const { user, profile, loading, profileLoading } = useUserContext();

  // Show loading while checking authentication state
  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
          <SkeletonLoader variant="form" />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <DocumentTitle />
        {user ? (
          // User is authenticated
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              backgroundColor: "#F2F2F7",
            }}
          >
            <Header />
            <Box
              sx={{
                flexGrow: 1,
                maxWidth: "1200px",
                mx: "auto",
                width: "100%",
                px: { xs: 2, sm: 3, md: 4 },
              }}
            >
              <Routes>
                {/* Show loading while checking profile */}
                {profileLoading ? (
                  <Route
                    path="*"
                    element={<SkeletonLoader variant="dashboard" />}
                  />
                ) : profile ? (
                  // User is authenticated and has a profile
                  <>
                    <Route
                      path="/complete-details"
                      element={<DriverDetailsCompletion />}
                    />
                    <Route
                      path="*"
                      element={
                        <DriverDetailsGuard profile={profile}>
                          <Routes>
                            <Route path="/" element={<Dashboard />} />
                            <Route path="/analytics" element={<Analytics />} />
                            <Route path="/profile" element={<ProfilePage />} />
                            <Route
                              path="/assigned-cars"
                              element={<AssignedCars />}
                            />
                            <Route
                              path="/how-it-works"
                              element={<HowItWorks />}
                            />
                            <Route
                              path="/privacy-policy"
                              element={<PrivacyPolicy />}
                            />
                            <Route
                              path="/terms-of-use"
                              element={<TermsOfUse />}
                            />
                            <Route
                              path="/data-deletion"
                              element={<DataDeletion />}
                            />
                            <Route path="/rewards" element={<Rewards />} />
                            <Route
                              path="/reports/add"
                              element={<AddReportPage />}
                            />
                            <Route
                              path="/reports/edit/:reportId"
                              element={<AddReportPage />}
                            />
                            <Route
                              path="/search-cars"
                              element={<CarSearch />}
                            />
                            <Route
                              path="/drive-requests"
                              element={
                                profile.user_type === "driver" ? (
                                  <DriverDriveRequests />
                                ) : (
                                  <OwnerDriveRequests />
                                )
                              }
                            />
                            <Route path="/cars/new" element={<CarForm />} />
                            <Route
                              path="/cars/:carId"
                              element={<CarDetailManagement />}
                            />
                            <Route
                              path="/cars/:carId/edit"
                              element={<CarForm />}
                            />
                            <Route path="/drivers" element={<DriverSearch />} />
                            <Route
                              path="/drivers/:driverId"
                              element={<DriverDetails />}
                            />
                            <Route path="/admin" element={<AdminDashboard />} />
                            <Route
                              path="/login"
                              element={<Navigate to="/" replace />}
                            />
                            <Route
                              path="*"
                              element={<Navigate to="/" replace />}
                            />
                          </Routes>
                        </DriverDetailsGuard>
                      }
                    />
                  </>
                ) : (
                  // User is authenticated but doesn't have a profile
                  <>
                    <Route
                      path="/complete-profile"
                      element={<ProfileCompletion />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to="/complete-profile" replace />}
                    />
                  </>
                )}
              </Routes>
            </Box>
            <Footer />
          </Box>
        ) : (
          // User is not authenticated, show login/signup pages
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: "100vh",
              backgroundColor: "#F2F2F7",
            }}
          >
            <Header />
            <Box sx={{ flexGrow: 1 }}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/login" element={<EnhancedLoginForm />} />
                <Route path="/signup" element={<EnhancedSignUpForm />} />
                <Route path="/signup-complete" element={<SignupComplete />} />
                <Route path="/how-it-works" element={<HowItWorks />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-use" element={<TermsOfUse />} />
                <Route path="/data-deletion" element={<DataDeletion />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        )}
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <UserProvider>
      <AppContent />
    </UserProvider>
  );
}

export default App;
